import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import SEO from "../../components/common/SEO";
import { generateBreadcrumbSchema } from "../../utils/seo/structuredData";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useUserLeagues } from "../../hooks/league/useFetchUserLeagues";
import { useEventsWithLeagueCount } from "../../hooks/events/useEventsWithLeagueCount";
import { isEventEligibleForLeague } from "../../utils/league/leagueHelpers";
import { setLeagueId } from "../../features/league/leagueReducer";
import Loading from "../../components/common/Loading";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import {
  resetLineup,
  resetLotn,
  resetAlternateSelections,
  resetAlternateLotn,
  resetProjectedTotal,
} from "../../features/lineup/lineupReducer";

import { EmptyLeaguesState } from "../../components/leagues";
import LeagueHeader from "../../components/league/LeagueHeader";
import GlobalCompetitionCard from "../../components/league/GlobalCompetitionCard";
import MyLeagues from "../../components/league/MyLeagues";
import ArchivedLeagues from "../../components/league/ArchivedLeagues";

const LeaguesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentLeagueId = useSelector((state) => state.league.leagueId);

  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [expandedLeagueId, setExpandedLeagueId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processedLeagues, setProcessedLeagues] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const archivedRef = useRef(null);
  const [archivedHeight, setArchivedHeight] = useState(0);

  const {
    data: dbUser,
    isLoading: isUserLoading,
    error: userError,
  } = useFetchUser();

  const {
    data: leaguesData,
    isLoading: isLeaguesLoading,
    error: leaguesError,
  } = useUserLeagues(dbUser?._id);

  const {
    events: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useEventsWithLeagueCount();

  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const { data: globalLeaderboard } = useLeagueMembers(
    globalLeagueId,
    1,
    10,
    "",
  );

  const {
    data: globalLeaderboardData,
    isLoading: isGlobalLeaderboardDataLoading,
  } = useLeagueMembers(globalLeagueId, 1, 100, "");

  // Measure archived content height for smooth animation
  useEffect(() => {
    if (archivedRef.current) {
      setArchivedHeight(archivedRef.current.scrollHeight);
    }
  }, [showArchived, leaguesData]);

  useEffect(() => {
    if (leaguesData?.memberOf && eventsData) {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      const leagues = [
        {
          _id: globalLeagueId,
          leagueName: "Global Competition",
          status: "active",
          isGlobal: true,
          isSpecial: true,
          startDate: startOfWeek,
          endDate: endOfWeek,
          totalMembers: globalLeaderboard?.total || 0,
          events: eventsData.filter((event) => {
            const eventDate = new Date(event.DateTime);
            return (
              eventDate >= today &&
              eventDate >= startOfWeek &&
              eventDate <= endOfWeek
            );
          }),
        },
      ];

      // Filter to only include active leagues
      const activeLeagues = leaguesData.memberOf.filter(
        (league) => league.status === "active",
      );

      activeLeagues.forEach((league) => {
        const leagueEvents = eventsData.filter((event) => {
          const eventDate = new Date(event.DateTime);
          return (
            eventDate >= today &&
            isEventEligibleForLeague(event.EventId, league, event.DateTime)
          );
        });

        leagues.push({
          ...league,
          isGlobal: false,
          isSpecial: false,
          events: leagueEvents,
          hasUpcomingEvents: leagueEvents.some((event) => {
            const eventDate = new Date(event.DateTime);
            const threeDaysFromNow = new Date(today);
            threeDaysFromNow.setDate(today.getDate() + 3);
            return eventDate <= threeDaysFromNow;
          }),
          isLive: leagueEvents.some((event) => {
            const eventDate = new Date(event.DateTime);
            const threeDaysFromNow = new Date(today);
            threeDaysFromNow.setDate(today.getDate() + 3);
            return eventDate <= threeDaysFromNow;
          }),
          currentEvent: leagueEvents[0]?.Name || null,
          memberCount: league.members?.length || 0,
          maxMembers: league.maxMembers || "∞",
        });
      });

      leagues.sort((a, b) => {
        if (a.isGlobal) return -1;
        if (b.isGlobal) return 1;
        if (a.hasUpcomingEvents && !b.hasUpcomingEvents) return -1;
        if (!a.hasUpcomingEvents && b.hasUpcomingEvents) return 1;
        if (a.events.length > 0 && b.events.length === 0) return -1;
        if (a.events.length === 0 && b.events.length > 0) return 1;
        return 0;
      });

      setProcessedLeagues(leagues);

      const privateLeagues = leagues.filter((league) => !league.isGlobal);

      if (!selectedLeagueId) {
        if (
          currentLeagueId &&
          privateLeagues.some((league) => league._id === currentLeagueId)
        ) {
          setSelectedLeagueId(currentLeagueId);
        } else if (privateLeagues.length > 0) {
          setSelectedLeagueId(privateLeagues[0]._id);
        }
      }

      setIsLoading(false);
    }
  }, [
    leaguesData,
    eventsData,
    globalLeaderboard,
    currentLeagueId,
    selectedLeagueId,
    globalLeagueId,
  ]);

  const handleCreateLeague = () => {
    navigate("/create-league");
  };

  const handleViewLeagueDetails = (leagueId) => {
    dispatch(setLeagueId(leagueId));
    navigate(`/league/${leagueId}`);
  };

  const handleViewSubmissions = (leagueId) => {
    dispatch(setLeagueId(leagueId));
    navigate("/scorecard");
  };

  const handleNavigateToGlobal = () => {
    if (currentLeagueId !== globalLeagueId) {
      dispatch(resetLineup());
      dispatch(resetAlternateSelections());
      dispatch(resetLotn());
      dispatch(resetAlternateLotn());
      dispatch(resetProjectedTotal());
    }
    dispatch({ type: "SET_LEAGUE_ID", payload: globalLeagueId });
    navigate(`/league/events`);
  };

  if (
    isUserLoading ||
    isLeaguesLoading ||
    isEventsLoading ||
    isGlobalLeaderboardDataLoading ||
    isLoading
  ) {
    return <Loading />;
  }

  const hasError = userError || leaguesError || eventsError;
  if (hasError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-light-grey">
        <div className="p-6 max-w-md text-center bg-white rounded-lg shadow-card">
          <div className="text-3xl text-deep-red mb-4">Error</div>
          <p className="text-medium-grey">
            {userError?.message ||
              leaguesError?.message ||
              eventsError?.message ||
              "Could not load league data."}
          </p>
          <button
            className="mt-4 px-4 py-2 bg-deep-red text-white rounded-md hover:bg-red-700 transition-colors"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  const privateLeagues = processedLeagues.filter((league) => !league.isGlobal);
  const globalLeague = processedLeagues.find((league) => league.isGlobal);

  // Get archived leagues
  const archivedLeagues = (leaguesData?.memberOf || []).filter(
    (league) => league.status === "archived" || league.status === "inactive",
  );

  if (processedLeagues.length === 0) {
    return (
      <EmptyLeaguesState
        onCreateLeague={handleCreateLeague}
        onGlobalCompetition={() => navigate("/")}
      />
    );
  }

  // Calculate stats for header
  const activeLeagueCount = privateLeagues.length;

  // Get next event date for countdown
  const nextEventDate =
    eventsData
      ?.filter((event) => new Date(event.DateTime) > new Date())
      ?.sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime))?.[0]
      ?.DateTime || null;

  const breadcrumbs = [
    { name: "Home", path: "/home" },
    { name: "Leagues", path: "/leagues" },
  ];

  return (
    <>
      <SEO
        title="My Leagues - UFC Fantasy Leagues | DayShift MMA"
        description="Manage your UFC fantasy leagues. Create private leagues with friends, join the global competition, view standings, and compete for the top spot. Build your lineup and dominate!"
        keywords="UFC fantasy leagues, MMA fantasy league, create fantasy league, private UFC league, fantasy MMA competition, league standings"
        url="/leagues"
        structuredData={generateBreadcrumbSchema(breadcrumbs)}
      />

      {/* New League Header */}
      <LeagueHeader
        activeLeagueCount={activeLeagueCount}
        selectedLeagueId={expandedLeagueId}
        userId={dbUser?._id}
        nextEventDate={nextEventDate}
      />

      <main className="px-4 py-4 space-y-6 pb-24">
        {/* My Leagues Section */}
        <MyLeagues
          leagues={privateLeagues}
          userId={dbUser?._id}
          onJoinLeague={handleCreateLeague}
          onViewLeagueDetails={handleViewLeagueDetails}
          onViewSubmissions={handleViewSubmissions}
          onLeagueSelect={(leagueId) => setExpandedLeagueId(leagueId)}
        />

        {/* Global Competition Card */}
        {globalLeague && (
          <GlobalCompetitionCard
            userRank={
              globalLeaderboardData?.data
                ? (() => {
                    const idx = globalLeaderboardData.data.findIndex(
                      (m) => m.userId === dbUser?._id,
                    );
                    return idx !== -1 ? idx + 1 : null;
                  })()
                : null
            }
            isLive={globalLeague.events?.length > 0}
            onNavigate={handleNavigateToGlobal}
          />
        )}

        {/* Archived Leagues Toggle */}
        <div className="pt-2">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full text-sm text-medium-grey hover:text-dark-grey transition-colors flex items-center justify-center gap-2 py-2"
          >
            {showArchived ? "Hide Archived Leagues" : "View Archived Leagues"}
            {archivedLeagues.length > 0 && (
              <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                {archivedLeagues.length}
              </span>
            )}
          </button>
        </div>

        {/* Archived Leagues - Animated */}
        <div
          style={{
            maxHeight: showArchived ? `${archivedHeight}px` : "0px",
            opacity: showArchived ? 1 : 0,
            transition:
              "max-height 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease-in-out",
          }}
          className="overflow-hidden"
        >
          <div ref={archivedRef}>
            <ArchivedLeagues
              leagues={archivedLeagues}
              onViewHistory={(leagueId) => {
                dispatch(setLeagueId(leagueId));
                navigate(`/league/${leagueId}`);
              }}
            />
          </div>
        </div>
      </main>
    </>
  );
};

export default LeaguesPage;
